import { useMemo } from "react";
import { getOverview } from "@ugg/shared/api/data-parser/champions/overview";
import { useChampionOverview } from "@ugg/shared/api/requests/champions/overview";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { RegionS } from "@ugg/shared/utils/region-helpers";
import { RankS } from "@ugg/shared/utils/rank-helpers";
import { RoleS } from "@ugg/shared/utils/role-helpers";

// ─────────────────────────────────────────────────────────────────────
// ARAM build data, for reuse on the ARAM Mayhem champion page.
//
// Mayhem has no build data of its own — no runes, no items, no skills — so the
// Skill Priority / Skill Path, Summoner Spells and Items modules all borrow
// ARAM's. The picks themselves carry over well enough to be useful; the win
// rates and match counts attached to them do NOT, so the caller passes
// `hideStats` to each module. See the note on that prop.
//
// Returns raw `OverviewInfo` slices rather than a bespoke shape: they feed
// straight into the shared modules' props, and anything this hook grows next
// (runes) is another field off the same fetch.
//
// Deliberately NOT useOverview(): that derives its queue type from the page
// (here, ARAM Mayhem — which has no overview endpoint) and pulls seven fetches
// per champion to populate the multi-build tabs. This needs one file, under a
// queue type the page isn't on, and two fields out of it.
//
// The params are pinned rather than passed through from the page's filters
// because they describe the ARAM endpoint's own shape, not the reader's
// selection: ARAM has a single world/overall/none bucket, and getData()
// forces rank+role to exactly these for ARAM anyway. Patch is the one thing
// that does vary, so it comes from the caller.
// ─────────────────────────────────────────────────────────────────────

export function useAramBuild({ championId, patch, ssr = false }: { championId: number; patch: string; ssr?: boolean }) {
  // `patch` is genuinely required — an empty one builds a URL against an
  // unresolved version and 404s, so skip until the caller has resolved it.
  // championId === 0 means the profile context hasn't resolved yet.
  const skip = !patch || !championId;

  const params = useMemo(
    () => ({
      queueType: QueueTypeS.ARAM,
      region: RegionS.WORLD,
      rank: RankS.OVERALL,
      role: RoleS.NONE,
      patch,
    }),
    [patch],
  );

  // useChampionOverview validates against CHAMPION_PAGES.OVERVIEW, whose
  // queueType option list includes ARAM — so the pinned queue type survives
  // validation and addresses the ARAM overview file. (Were it absent from that
  // list, validation would silently swap in the ranked-solo default and this
  // would fetch the wrong mode's data without erroring.)
  const { data, loading, error } = useChampionOverview(championId, { params, ssr, skip });

  // getOverview indexes the region_rank_role bucket out of the cleaned
  // payload, applying the same ARAM rank/role coercion the params above pin.
  const overview = data ? getOverview(data, params) : null;

  return {
    skillPriority: overview?.rec_skills ?? null,
    skillPath: overview?.rec_skill_path ?? null,
    evolutionPath: overview?.rec_skill_evolve_path ?? null,
    summonerSpells: overview?.rec_summoner_spells ?? null,
    startingItems: overview?.rec_starting_items ?? null,
    coreItems: overview?.rec_core_items ?? null,
    itemOptions1: overview?.item_options_1 ?? null,
    itemOptions2: overview?.item_options_2 ?? null,
    itemOptions3: overview?.item_options_3 ?? null,
    itemOptions4: overview?.item_options_4 ?? null,
    t3Boots: overview?.t3_boots_options ?? null,
    loading,
    error,
  };
}
